import { Decoration } from "prosemirror-view";
import { hasAttr, hasType } from "../../core/helpers";
/**
 * Return an array of node decorations applying the decorator function to the nodes inside the
 * section delimited by `heading` node.
 *
 * @param state The current editor state
 * @param heading The heading node to create the decorations for
 * @param decorator The decorator function that will be used to decorate the nodes.
 * @param inclusive Whether the heading node should be decorated as well.
 */
export function createSectionDecorations(state, heading, decorator, inclusive) {
    const decorations = [];
    const { level } = heading.node.attrs;
    const resolvedPos = state.doc.resolve(heading.pos);
    const { parent, parentOffset } = resolvedPos;
    const parentAbsolutePosition = resolvedPos.start();
    // The start of the heading section (the position immediately after the node), relative to parent
    const start = parentOffset + heading.node.nodeSize;
    // Find the end of the heading section
    const nextStopPosition = findNextHeadingStopPosition(state, parent, start, level);
    parent.nodesBetween(inclusive ? parentOffset : start, nextStopPosition, (node, pos) => {
        const decorationAttrs = decorator(heading.node.attrs, node);
        if (decorationAttrs) {
            decorations.push(Decoration.node(parentAbsolutePosition + pos, parentAbsolutePosition + pos + node.nodeSize, decorationAttrs));
        }
        // Only apply to immediate descendants
        return false;
    });
    return decorations;
}
/**
 * Find the index position of the next heading node of the same or greater level than the parameter.
 * The index position is relative to the `parent` node.
 *
 * @param state The current editor state
 * @param parent The parent node container where the collapsing will be restricted to.
 * This is the `doc` in the most common scenario but can be another container, like a `blockquote`
 * @param start Look all nodes after this position, relative to `parent`
 * @param level Find heading nodes greater or equal this level
 */
export function findNextHeadingStopPosition(state, parent, start, level) {
    let stopHeadingPos = null;
    parent.nodesBetween(start, parent.content.size, // Look until the end of the parent node
    (node, pos) => {
        if (!stopHeadingPos &&
            hasType(node, state.schema.nodes.heading) &&
            hasAttr(node, "level") &&
            level >= node.attrs.level) {
            stopHeadingPos = pos;
        }
        // Only look for immediate descendants
        return false;
    });
    // If nothing was found, consider the end of the parent node as the next stop position
    return stopHeadingPos || parent.content.size;
}
export function findChildHeadings(state, heading) {
    const { level } = heading.node.attrs;
    const resolvedPos = state.doc.resolve(heading.pos);
    const { parent, parentOffset } = resolvedPos;
    // The start of the heading section (the position immediately after the node), relative to parent
    const start = parentOffset + heading.node.nodeSize;
    const nextStopPosition = findNextHeadingStopPosition(state, parent, start, level);
    const nodes = [];
    parent.nodesBetween(start, nextStopPosition, (node, pos) => {
        if (hasType(node, state.schema.nodes.heading) &&
            node.attrs.level > level) {
            nodes.push({ node, pos });
        }
    });
    return nodes;
}
